import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Divider,
  IconButton,
  CircularProgress,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
} from "@mui/material";
import * as XLSX from "xlsx"; // For Excel parsing
import axios from "axios"; // For sending parsed data to backend
import {
  ORG_FETCH,
  ORG_STATS,
  ORG_UPLOAD,
  ORG_UPLOAD_REPLACE,
} from "../../api/orgAuth";
import { FiUpload } from "react-icons/fi";
import { USER } from "../../api/vsc";
import { useParams, useHistory } from "react-router-dom";

const HalfCircleProgress = ({ percentage, color }) => {
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width="100%" viewBox="0 0 120 60">
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="transparent"
        stroke="#c1ff72"
        strokeWidth="7"
        strokeDasharray={circumference}
        transform="rotate(90 60 60)"
      />
      <circle
        cx="60"
        cy="60"
        r={radius}
        fill="transparent"
        stroke={color}
        strokeWidth="7"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform="rotate(90 60 60)"
        style={{ transition: "stroke-dashoffset 0.6s ease" }}
      />
    </svg>
  );
};

const formatTime = (time) => {
  time = String(time || "").trim();
  if (!time) return "";

  // If user typed "10.30" instead of "10:30", fix it
  const cleanTime = time.replace(".", ":");

  if (!cleanTime.includes(":")) {
    return "Invalid Time";
  }

  let [hour, minute] = cleanTime.split(":").map(Number);

  if (isNaN(hour) || isNaN(minute) || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return "Invalid Time";
  }

  const isPM = hour >= 12;
  const formattedHour = hour % 12 === 0 ? 12 : hour % 12;
  const suffix = isPM ? "PM" : "AM";
  return `${formattedHour}:${minute < 10 ? `0${minute}` : minute} ${suffix}`;
};

const formatTime2 = (fraction) => {
  if (typeof fraction !== "number" || fraction < 0 || fraction > 1) return "";
  const totalMinutes = Math.round(fraction * 24 * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}`;
};

function convertAndFormatExcelDate(excelDate, format = "dd-mm-yyyy") {
  if (typeof excelDate !== "number" || excelDate <= 0) return "";
  // Use the robust XLSX parser which correctly handles the 1900 leap year bug
  const jsDate = XLSX.SSF.parse_date_code(excelDate);
  if (!jsDate) return "";
  let day = jsDate.d;
  let month = jsDate.m;
  let year = jsDate.y;
  if (day < 10) day = "0" + day;
  if (month < 10) month = "0" + month;
  return format.replace("dd", day).replace("mm", month).replace("yyyy", year);
}

const OrgDashboard = ({ isAuth, setIsAuth, data }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [swotPeople, setSwotPeople] = useState([]);
  const params = useParams();
  const [refresh, setRefresh] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [csvDownloadUrl, setCsvDownloadUrl] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stats, setStats] = useState({
    communication: "0.0",
    knowledge: "0.0",
    experience: "0.0",
    adaptability: "0.0",
    analytical: "0.0",
    creativeThinking: "0.0",
  });
  const navigate = useHistory();

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleClick = () =>
    navigate.push("/allstudents", { state: { swotPeople } });

  useEffect(() => {
    axios
      .get(USER, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((response) => setUser(response.data))
      .catch((error) => console.error(error));

    axios
      .get(ORG_FETCH + params.id)
      .then((res) => setSwotPeople(res.data.data))
      .catch((err) => console.error(err));

    const fetchRatingPercentages = async () => {
      try {
        const response = await axios.get(ORG_STATS + params.id);
        if (response.data.stats) setStats(response.data.stats);
      } catch (error) {
        console.error("Error fetching rating stats:", error);
      }
    };

    fetchRatingPercentages();
    setRefresh(false);
  }, [refresh, params.id]);

  const handleFileUpload = (event, from) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      setFile(uploadedFile);
      const fileType = uploadedFile.name.split(".").pop().toLowerCase();

      // Verification for supported types
      const supportedTypes = ["csv", "xlsx", "xls"];
      if (!supportedTypes.includes(fileType)) {
        alert("Please upload a valid CSV or Excel file.");
        return;
      }

      const processParsedData = (data) => {
        const transformedData = transformDataForSchema(data);
        if (from === "1") {
          sendDataToBackend(transformedData);
        } else {
          sendDataToBackend2(transformedData);
        }
      };

      // Use XLSX for BOTH CSV and Excel
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          /* 
             XLSX.read automatically detects if the file is CSV or Excel 
             based on the file content/buffer 
          */
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convert to JSON (This replaces Papa.parse functionality)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: "", // Ensures empty cells are returned as empty strings
          });

          processParsedData(jsonData);
        } catch (error) {
          console.error("Error parsing file:", error);
          alert("Failed to parse file. Please check the format.");
        }
      };
      reader.readAsArrayBuffer(uploadedFile);
    }
  };
  const getRowValue = (row, possibleKeys) => {
    const keys = Object.keys(row);
    for (const key of keys) {
      const normalizedKey = key.trim().toLowerCase();
      if (possibleKeys.map(pk => pk.toLowerCase()).includes(normalizedKey)) {
        return row[key];
      }
    }
    return "";
  };
  const transformDataForSchema = (data) => {
    return data.map((row) => {
      // 1. Get raw values using the flexible helper
      const rawName = getRowValue(row, ["Name", "Student Name"]);
      const rawEmail = getRowValue(row, ["Email ID", "Email", "EmailID"]);
      const rawWhatsapp = getRowValue(row, ["WhatsappNumber", "Whatsapp", "Phone"]);
      const rawDate = getRowValue(row, ["Date", "Booking Date"]);
      const rawTime = getRowValue(row, ["Time", "Booking Time"]);

      // 2. Handle Date (If number, convert from Excel. If string, use as is)
      let formattedDate = "";
      if (typeof rawDate === "number") {
        formattedDate = convertAndFormatExcelDate(rawDate);
      } else {
        formattedDate = String(rawDate || "").trim();
      }

      // 3. Handle Time (Logic Fix)
      let formattedTime = "";
      if (typeof rawTime === "number") {
        // It's an Excel fraction (e.g. 0.75)
        formattedTime = formatTime(formatTime2(rawTime));
      } else if (typeof rawTime === "string" && rawTime.trim() !== "") {
        // It's already a string (e.g. "14:30")
        formattedTime = formatTime(rawTime);
      }

      return {
        name: String(rawName || "").trim(),
        email: String(rawEmail || "").trim(),
        whatsappNumber: String(rawWhatsapp || "").trim(),
        bookingDate: formattedDate,
        time: formattedTime || "Invalid Time",
      };
    });
  };

  const handleUnavailableBookings = (response) => {
    if (response.data.unavailableBookings?.length > 0) {
      const unavailableBookings = response.data.unavailableBookings;
      const ws = XLSX.utils.json_to_sheet(unavailableBookings);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Unavailable Bookings");
      const csvFile = XLSX.write(wb, { bookType: "csv", type: "array" });
      const blob = new Blob([csvFile], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      setCsvDownloadUrl(url);
      setSnackbarOpen(true);
    }
  };

  const sendDataToBackend = (parsedData) => {
    setLoading(true);
    axios
      .post(ORG_UPLOAD + window.localStorage.id, parsedData)
      .then((response) => {
        setRefresh(true);
        setLoading(false);
        handleUnavailableBookings(response);
      })
      .catch((error) => {
        setLoading(false);
        console.error("Error uploading data:", error);
      });
  };

  const sendDataToBackend2 = (parsedData) => {
    setLoading(true);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
      }
    }, 300);
    axios
      .post(ORG_UPLOAD_REPLACE + window.localStorage.id, parsedData)
      .then((response) => {
        setRefresh(true);
        setLoading(false);
        setUploadProgress(0);
        handleUnavailableBookings(response);
      })
      .catch((error) => {
        setLoading(false);
        setUploadProgress(0);
        console.error("Error uploading data:", error);
      });
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);
  const triggerFileInput = () => document.getElementById("file-upload").click();
  const triggerFileInput2 = () =>
    document.getElementById("file-upload1").click();

  const handleDownload = () => {
    const data = [
      {
        Name: "Astha",
        WhatsappNumber: "9999999999",
        "Email ID": "astha@gmail.com",
        RollNo: "764546",
        Date: "15-01-2025",
        Time: "18:15",
      },
    ];
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(workbook, "sample_sheet.xlsx");
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        fontFamily: "Arial",
        mt: { xs: "10px", md: "10px" },
        backgroundColor: "#fff",
      }}
    >
      <Typography
        variant="h4"
        sx={{ textAlign: "center", fontWeight: "bold", mb: 3 }}
      >
        College Industry Collab
      </Typography>

      {/* Top Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 3,
        }}
      >
        {/* Snapshot Box */}
        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <span
              style={{
                backgroundColor: "#F5C663",
                padding: "5px 10px",
                borderRadius: "10px",
              }}
            >
              Snapshot
            </span>
          </Typography>
          <Box
            sx={{
              border: "1px solid #000",
              p: "20px",
              width: "100%",
              borderRadius: "10px",
              height: { md: "calc(100% - 36px)" },
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-around",
                alignItems: "center",
                height: "100%",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {user?.totalAppearingStudents || 0}
                </Typography>
                <Typography variant="h6">Enrolled</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {swotPeople.filter((swot) => swot.bookingDate).length}
                </Typography>
                <Typography variant="h6">Bookings</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {swotPeople.filter((swot) => swot.ratingOverall).length}
                </Typography>
                <Typography variant="h6">Appeared</Typography>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                  {
                    swotPeople.filter((swot) => swot.ratingOverall * 10 > 80)
                      .length
                  }
                </Typography>
                <Typography variant="h6">Above 80%</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Upload Box */}
        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            <span
              style={{
                backgroundColor: "#F5C663",
                padding: "5px 10px",
                borderRadius: "10px",
              }}
            >
              Students Appearing for Interview
            </span>
          </Typography>
          <Box
            sx={{
              border: "1px solid #000",
              p: "20px",
              width: "100%",
              borderRadius: "10px",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-evenly",
                alignItems: "center",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Box sx={{ textAlign: "center" }}>
                <input
                  type="file"
                  accept=".csv, .xlsx, .xls"
                  style={{ display: "none" }}
                  id="file-upload"
                  onChange={(e) => handleFileUpload(e, "1")}
                />
                <label htmlFor="file-upload">
                  <IconButton
                    onClick={triggerFileInput}
                    sx={{ p: "10px 20px" }}
                    disabled={loading}
                  >
                    {loading ? (
                      <CircularProgress />
                    ) : (
                      <FiUpload style={{ fontSize: "80px" }} />
                    )}
                  </IconButton>
                  <Typography>Add Students</Typography>
                </label>
              </Box>
              <Box sx={{ textAlign: "center" }}>
                <input
                  type="file"
                  accept=".csv, .xlsx, .xls"
                  style={{ display: "none" }}
                  id="file-upload1"
                  onChange={(e) => handleFileUpload(e, "2")}
                />
                <label htmlFor="file-upload1">
                  <IconButton
                    onClick={triggerFileInput2}
                    sx={{ p: "10px 20px" }}
                    disabled={loading}
                  >
                    {loading ? (
                      <Box
                        sx={{ position: "relative", display: "inline-flex" }}
                      >
                        <CircularProgress
                          variant="determinate"
                          value={uploadProgress}
                          size={80}
                          thickness={5}
                        />
                        <Box
                          sx={{
                            top: 0,
                            left: 0,
                            bottom: 0,
                            right: 0,
                            position: "absolute",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Typography
                            variant="caption"
                            component="div"
                            color="textSecondary"
                          >{`${uploadProgress}%`}</Typography>
                        </Box>
                      </Box>
                    ) : (
                      <FiUpload style={{ fontSize: "80px" }} />
                    )}
                  </IconButton>
                  <Typography>Replace All</Typography>
                </label>
              </Box>
              <Button
                variant="contained"
                sx={{ background: "#6a58c6" }}
                onClick={handleDownload}
              >
                Download Sample
              </Button>
            </Box>
            <Typography
              variant="body2"
              sx={{ mt: "10px", textAlign: "center" }}
            >
              Upload CSV/Excel:{" "}
              <strong>
                Name, WhatsappNumber, Email ID, Roll No, Date, Time
              </strong>
              . (Column names are case-sensitive).
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Bottom Section */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
        }}
      >
        {/* SWOT Analysis Box */}
        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              <span
                style={{
                  backgroundColor: "#F5C663",
                  padding: "5px 10px",
                  borderRadius: "10px",
                }}
              >
                SWOT Analysis of People
              </span>
            </Typography>
            <Button
              sx={{ background: "#6a58c6", color: "#fff" }}
              onClick={handleClick}
            >
              View All
            </Button>
          </Box>
          <Box
            sx={{
              border: "1px solid #000",
              p: "20px",
              width: "100%",
              borderRadius: "10px",
              boxSizing: "border-box",
            }}
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Sl.</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Report</TableCell>
                    <TableCell>Rating</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {swotPeople
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((person, index) => (
                      <TableRow key={person._id || index}>
                        <TableCell>{index + 1 + page * rowsPerPage}</TableCell>
                        <TableCell>{person.name}</TableCell>
                        <TableCell>
                          {person.bookingDate || "Not Booked"}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="text"
                            size="small"
                            sx={{
                              textTransform: "none",
                              textDecoration: "underline",
                              color: "#000",
                            }}
                            disabled={!person.ratingOverall}
                            href={`/studentreport/${person._id}`}
                          >
                            View Report
                          </Button>
                        </TableCell>
                        <TableCell>
                          {person.ratingOverall
                            ? "⭐".repeat(Math.round(person.ratingOverall / 2))
                            : "No Rating"}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={swotPeople.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Box>

        {/* Consolidated Report Box */}
        <Box sx={{ width: { xs: "100%", md: "50%" } }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            <span
              style={{
                backgroundColor: "#F5C663",
                padding: "5px 10px",
                borderRadius: "10px",
              }}
            >
              Consolidated Report
            </span>
          </Typography>
          <Box
            sx={{
              border: "1px solid #000",
              p: "20px",
              width: "100%",
              borderRadius: "10px",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "space-around", sm: "space-between" },
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.communication * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.communication * 10}% of students <br /> demonstrated
                  excellent communication skills
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.analytical * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.analytical * 10}% of students <br /> exhibited
                  leadership potential
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.adaptability * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.adaptability * 10}% of students <br /> demonstrated
                  strong adaptability skills
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ my: "20px" }} />
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "space-around", sm: "space-between" },
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.knowledge * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.knowledge * 10}% of students <br /> need improvement in
                  technical tools
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.creativeThinking * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.creativeThinking * 10}% of students <br /> need
                  improvement in critical thinking
                </Typography>
              </Box>
              <Box
                sx={{
                  textAlign: "center",
                  width: { xs: "80%", sm: "45%", md: "30%" },
                }}
              >
                <HalfCircleProgress
                  percentage={stats.experience * 10}
                  color="#00bf63"
                />
                <Typography sx={{ mt: "10px", fontSize: "0.8rem" }}>
                  {stats.experience * 10}% of students <br /> demonstrated
                  strong experience
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        message="Some bookings were unavailable"
        action={
          <Button
            color="secondary"
            size="small"
            href={csvDownloadUrl}
            download="unavailable_bookings.csv"
          >
            Download CSV
          </Button>
        }
      />
    </Box>
  );
};

export default OrgDashboard;
